import React, { useCallback } from 'react';
import { Type, Image, Square, Circle, MoveUpRight, LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useAppStore, useAnnotationState, AnnotationType } from '@/stores/appStore';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Separator } from '@/components/ui/separator';

interface AnnotationTool {
  id: AnnotationType;
  icon: LucideIcon | 'line'; // 'line' is custom SVG
  label: string;
  shortcut: string;
  group: 'drawing' | 'content';
}

const ANNOTATION_TOOLS: AnnotationTool[] = [
  { id: 'line', icon: 'line', label: 'Line', shortcut: 'L', group: 'drawing' },
  { id: 'rectangle', icon: Square, label: 'Rectangle', shortcut: 'R', group: 'drawing' },
  { id: 'circle', icon: Circle, label: 'Circle', shortcut: 'C', group: 'drawing' },
  { id: 'arrow', icon: MoveUpRight, label: 'Arrow', shortcut: 'A', group: 'drawing' },
  { id: 'text', icon: Type, label: 'Text', shortcut: 'T', group: 'content' },
  { id: 'image', icon: Image, label: 'Image', shortcut: 'I', group: 'content' },
];

// Custom Line Icon Component
const LineIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 16 16"
    className={cn('w-4 h-4', className)}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
  >
    <line x1="2" y1="14" x2="14" y2="2" />
  </svg>
);

export function AnnotationToolbar() {
  const { activeAnnotationType, isAnnotating } = useAnnotationState();
  const setActiveAnnotationType = useAppStore(state => state.setActiveAnnotationType);

  // Get other placement modes to clear them when annotation is activated
  const isControlPlacementMode = useAppStore(
    state => state.ui.toolbar.activeMode === 'control-placement',
  );
  const isProductPlacementMode = useAppStore(
    state => state.ui.toolbar.activeMode === 'product-placement',
  );
  const isCircuitMode = useAppStore(state => state.ui.toolbar.activeMode === 'circuit');
  const selectedTool = useAppStore(state => state.ui.toolbar.activeTool);

  const onControlTypeSelect = useAppStore(state => state.ui.toolbar.activeMode);
  const setActiveTool = useAppStore(state => state.setActiveTool);
  const setActiveMode = useAppStore(state => state.setActiveMode);

  const handleToolSelect = useCallback(
    (toolId: AnnotationType) => {
      // Clear other modes when activating annotation
      if (isControlPlacementMode) {
        setActiveMode('default');
      }
      if (isProductPlacementMode) {
        setActiveMode('default');
      }
      if (isCircuitMode) {
        setActiveMode('default');
      }

      // If clicking the same tool, deactivate it
      if (activeAnnotationType === toolId) {
        setActiveAnnotationType(null);
      } else {
        setActiveAnnotationType(toolId);
        // Set select tool when annotating
        if (selectedTool !== 'select') {
          setActiveTool('select');
        }
      }
    },
    [
      activeAnnotationType,
      setActiveAnnotationType,
      isControlPlacementMode,
      isProductPlacementMode,
      isCircuitMode,
      setActiveMode,
      selectedTool,
      setActiveTool,
    ],
  );

  // Keyboard shortcuts removed per user request

  // Group tools by type
  const drawingTools = ANNOTATION_TOOLS.filter(t => t.group === 'drawing');
  const contentTools = ANNOTATION_TOOLS.filter(t => t.group === 'content');

  const renderToolButton = (tool: AnnotationTool) => {
    const isActive = activeAnnotationType === tool.id;
    const IconComponent = tool.icon === 'line' ? LineIcon : tool.icon;

    return (
      <Tooltip key={tool.id}>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleToolSelect(tool.id)}
            className={cn(
              'w-8 h-8 p-0 flex items-center justify-center rounded-md transition-all duration-150',
              isActive
                ? 'bg-white shadow-sm ring-1 ring-gray-200 text-gray-900 hover:bg-white'
                : 'text-gray-600 hover:bg-gray-200/50 hover:text-gray-900',
            )}
          >
            {tool.icon === 'line' ? (
              <IconComponent className={cn(isActive ? 'text-gray-900' : 'text-gray-600')} />
            ) : (
              <IconComponent className="h-4 w-4" strokeWidth={1.5} />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="text-xs font-medium">
          {tool.label}
        </TooltipContent>
      </Tooltip>
    );
  };

  return (
    <TooltipProvider delayDuration={300}>
      <div className="flex items-center gap-1 bg-gray-50/80 backdrop-blur-sm rounded-lg px-2 py-1.5">
        {/* Drawing Tools */}
        {drawingTools.map(tool => renderToolButton(tool))}

        {/* Separator */}
        <Separator orientation="vertical" className="h-5 w-px bg-gray-300 mx-0.5" />

        {/* Content Tools */}
        {contentTools.map(tool => renderToolButton(tool))}
      </div>
    </TooltipProvider>
  );
}
